import { Search } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { cn } from "@repo/utils";
import { ClearButton } from "../button/components/clear-button.js";

export type SearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  debounceMs?: number;
  disabled?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  clearable?: boolean;
  icon?: React.ReactNode;
  size?: "sm" | "md" | "lg";
};

export const SearchInput = ({
  value,
  onChange,
  placeholder = "Search...",
  className,
  debounceMs = 300,
  disabled = false,
  onFocus,
  onBlur,
  autoFocus = false,
  clearable = true,
  icon = <Search className="h-5 w-5" />,
  size = "md",
}: SearchInputProps) => {
  const [localValue, setLocalValue] = useState(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (newValue: string) => {
    setLocalValue(newValue);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      onChange(newValue);
    }, debounceMs);
  };

  const handleClear = () => {
    setLocalValue("");
    onChange("");
    inputRef.current?.focus();
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-5 py-3 text-lg",
  };

  return (
    <div className={cn("relative", className)}>
      {icon && (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
          {icon}
        </div>
      )}

      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        className={cn(
          "w-full rounded-md border border-gray-300 transition-colors",
          "focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none",
          "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500",
          sizeClasses[size],
          {
            "pl-10": icon,
            "pr-10": clearable && localValue,
          }
        )}
      />

      {clearable && localValue && !disabled && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <ClearButton
            aria-label="Clear search"
            onClick={handleClear}
            className="rounded-none hover:bg-transparent active:bg-transparent active:text-gray-800"
          />
        </div>
      )}
    </div>
  );
};
